import { Table } from "antd";
import Column from "antd/lib/table/Column";
import Amount from "components/Amount";
import FuelingProcessStatus from "components/FuelingProcessStatus";
import FuelProductName from "components/FuelProductName";
import Litres from "components/Litres";
import MomentItem from "components/MomentItem";
import RejectionReason from "components/RejectionReason";
import React from "react";
import intl from "utils/intl";

import FuelingProcessTableItem from "./FuelingProcessTableItem";
import messages from "./messages";
import FuelingProcessTableProps from "./props";
import useFilterItems from "./useFilterItems";
import { useSearchItems } from "./useSearchItems";

const createTimeSorter = (
  a: FuelingProcessTableItem,
  b: FuelingProcessTableItem
): number => a.createTime.unix() - b.createTime.unix();

const FuelingProcessTable: React.FC<FuelingProcessTableProps> = (
  props: FuelingProcessTableProps
) => {
  const filterItems = useFilterItems(props.processes);

  const {
    cardNumberColumnSearchProps,
    stationColumnSearchProps,
    fuelingProcessIdColumnSearchProps,
    partnerRefuelingIdColumnSearchProps,
    batchColumnSearchProps,
  } = useSearchItems();

  return (
    <>
      <Table
        dataSource={props.processes}
        rowKey="fuelingProcessId"
        scroll={{ x: "max-content" }}
      >
        <Column
          title={intl.formatMessage(messages.createTimeTitle)}
          sorter={createTimeSorter}
          defaultSortOrder="descend"
          render={(record: FuelingProcessTableItem): React.ReactNode => (
            <MomentItem value={record.createTime} />
          )}
        />
        <Column
          title={intl.formatMessage(messages.stateTitle)}
          filters={filterItems.states}
          filterSearch={true}
          onFilter={(value, record: FuelingProcessTableItem) =>
            record.state === value
          }
          render={(record: FuelingProcessTableItem): React.ReactNode => (
            <FuelingProcessStatus
              state={record.state}
              id={`process-state-${record.fuelingProcessId}`}
            />
          )}
        />
        <Column
          title={intl.formatMessage(messages.partnerIdTitle)}
          dataIndex="partnerId"
          filters={filterItems.partners}
          filterSearch={true}
          onFilter={(value, record: FuelingProcessTableItem) =>
            record.partnerId === value
          }
        />
        <Column
          title={intl.formatMessage(messages.supplierIdTitle)}
          dataIndex="supplierId"
          filters={filterItems.suppliers}
          filterSearch={true}
          onFilter={(value, record: FuelingProcessTableItem) =>
            record.supplierId === value
          }
        />
        <Column
          title={intl.formatMessage(messages.stationIdTitle)}
          {...stationColumnSearchProps}
        />
        <Column
          title={intl.formatMessage(messages.fuelingProcessIdTitle)}
          {...fuelingProcessIdColumnSearchProps}
        />
        <Column
          title={intl.formatMessage(messages.productIdTitle)}
          filters={filterItems.productIds}
          filterSearch={true}
          onFilter={(value, record: FuelingProcessTableItem) =>
            record.productId === value
          }
          render={(record: FuelingProcessTableItem): React.ReactNode => (
            <FuelProductName
              productId={record.productId}
              vendor={record.vendor}
            />
          )}
        />
        <Column
          align="right"
          title={intl.formatMessage(messages.receiptPriceTitle)}
          render={(record: FuelingProcessTableItem): React.ReactNode =>
            record.receiptPrice && (
              <Amount
                value={record.receiptPrice}
                id={`rctprice-${record.fuelingProcessId}`}
              />
            )
          }
        />
        <Column
          align="right"
          title={intl.formatMessage(messages.partnerReceiptPriceTitle)}
          render={(record: FuelingProcessTableItem): React.ReactNode =>
            record.partnerReceiptPrice && (
              <Amount
                value={record.partnerReceiptPrice}
                id={`rctprice-${record.fuelingProcessId}`}
              />
            )
          }
        />
        <Column
          align="right"
          title={intl.formatMessage(messages.requestedAmountTitle)}
          render={(record: FuelingProcessTableItem): React.ReactNode =>
            record.requestedAmount && (
              <Amount
                value={record.requestedAmount}
                id={`reqamt-${record.fuelingProcessId}`}
              />
            )
          }
        />
        <Column
          align="right"
          title={intl.formatMessage(messages.receiptAmountTitle)}
          render={(record: FuelingProcessTableItem): React.ReactNode =>
            record.receiptAmount && (
              <Amount
                value={record.receiptAmount}
                id={`rctamt-${record.fuelingProcessId}`}
              />
            )
          }
        />
        <Column
          align="right"
          title={intl.formatMessage(messages.partnerReceiptAmountTitle)}
          render={(record: FuelingProcessTableItem): React.ReactNode =>
            record.partnerReceiptAmount && (
              <Amount
                value={record.partnerReceiptAmount}
                id={`rctamt-${record.fuelingProcessId}`}
              />
            )
          }
        />
        <Column
          align="right"
          title={intl.formatMessage(messages.receiptQuantityTitle)}
          render={(record: FuelingProcessTableItem): React.ReactNode =>
            record.quantity && <Litres value={record.quantity} />
          }
        />
        <Column
          title={intl.formatMessage(messages.rejectionReasonTitle)}
          filters={filterItems.rejectionReasons}
          filterSearch={true}
          onFilter={(value, record: FuelingProcessTableItem) =>
            record.rejectionReason === value
          }
          render={(record: FuelingProcessTableItem): React.ReactNode =>
            record.rejectionReason && (
              <RejectionReason
                code={record.rejectionReason}
                id={`rejection-reason-${record.fuelingProcessId}`}
              />
            )
          }
        />
        <Column
          title={intl.formatMessage(messages.partnerRefuelingIdTitle)}
          {...partnerRefuelingIdColumnSearchProps}
        />
        <Column
          title={intl.formatMessage(messages.clientIdTitle)}
          dataIndex="clientId"
          filters={filterItems.clients}
          filterSearch={true}
          onFilter={(value, record: FuelingProcessTableItem) =>
            record.clientId === value
          }
        />
        <Column
          title={intl.formatMessage(messages.cardNumberTitle)}
          {...cardNumberColumnSearchProps}
        />
        <Column
          title={intl.formatMessage(messages.batchTitle)}
          {...batchColumnSearchProps}
        />
        <Column
          title={intl.formatMessage(messages.actionTitle)}
          fixed={"right"}
          render={(record: FuelingProcessTableItem): React.ReactNode => {
            /* eslint-disable-next-line*/
              return (<a onClick={() => props.viewDetail(record.fuelingProcessId)} id={`details-${record.fuelingProcessId}`}>
                {intl.formatMessage(messages.viewDetails)}
              </a>
            );
          }}
        />
      </Table>
    </>
  );
};

export default FuelingProcessTable;
